/**
 * Whether a notification is unread — one rule, shared by the bell row, the
 * bell's unread count and the Inbox page.
 *
 * Each of those used to decide separately: the bell by `readAt`, the Inbox by
 * `isRead`. They disagreed on exactly the case that matters — an item somebody
 * responded to (an APPROVED welcome, say) without opening it first. It carried a
 * state that means "read" but no read time, so it was read in one place and bold
 * in the other, on every refresh.
 *
 * Responding to something is reading it, so any of these makes an item read:
 * a read time, the server saying it is read, a responded state, or a recorded
 * response.
 */
import type { Notification } from '../../types';

/** States in which the person has, by definition, seen the notification. */
const READ_STATES = new Set(['READ', 'ACTIONED', 'ESCALATED', 'COMPLETED']);

export const isNotificationRead = (n: Notification): boolean =>
  !!n.readAt
  || n.isRead === true
  || READ_STATES.has(String(n.state || '').toUpperCase())
  || !!n.selectedAction;

export const isNotificationUnread = (n: Notification): boolean => !isNotificationRead(n);
